import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, Edit, Send, Trash2, Plus } from 'lucide-react';
import { useTitle } from '../context/TitleContext';
import { loadTemplates, saveTemplates, DocumentTemplate } from '../models/templateModels';

const MyTemplates = () => {
  const { t } = useTranslation();
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);

  useEffect(() => {
    setTitle(t('myTemplates'));
    document.title = t('myTemplates');
  }, [setTitle, t]);

  useEffect(() => {
    // Load all templates from localStorage
    const allTemplates = loadTemplates();
    setTemplates(allTemplates);
  }, []);

  const handleDelete = (templateId: string) => {
    if (confirm(t('confirmDeleteTemplate'))) {
      const updatedTemplates = templates.filter(t => t.id !== templateId);
      saveTemplates(updatedTemplates);
      setTemplates(updatedTemplates);
    }
  };

  const handleEdit = (templateId: string) => {
    navigate(`/templates/${templateId}/edit`);
  };

  const handleSend = (templateId: string) => {
    navigate(`/templates/${templateId}/send`);
  };

  const handleCreateNew = () => {
    navigate('/requests/new');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('myTemplates')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('myTemplatesDescription')}
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <Plus className="h-5 w-5 mr-2" />
          {t('createNewTemplate')}
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center border border-gray-200 dark:border-gray-700">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('noTemplatesYet')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('noTemplatesYetDescription')}
          </p>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('createFirstTemplate')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {template.name}
                    </h3>
                    {template.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {template.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <span>
                    {template.items?.filter(item => item.isRequired).length || 0} {t('required')}
                  </span>
                  <span>•</span>
                  <span>
                    {template.items?.filter(item => !item.isRequired).length || 0} {t('optional')}
                  </span>
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-500 mb-4">
                  {t('lastUpdated')}: {new Date(template.updatedAt || template.createdAt).toLocaleDateString()}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleSend(template.id)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title={t('sendRequest')}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {t('send')}
                  </button>
                  <button
                    onClick={() => handleEdit(template.id)}
                    className="inline-flex items-center justify-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                    title={t('editTemplate')}
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="inline-flex items-center justify-center px-3 py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 text-sm rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                    title={t('deleteTemplate')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyTemplates;
